import React, { useEffect, useState } from 'react'

import { Box, Button, Grid, Tooltip, Zoom } from '@mui/material'
import {
  useCoaGetPdfUrlLazyQuery
} from '../../../api-services/asset-holdings/adminCellOwnerAgreementApi'
import { type EditAssetHolderProps } from './EditAssetHolderForm'
import { DataGrid, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid'
import { ProjectStatusChip } from '../../components/chips/ProjectStatusChip'
import CurrencyCodeChip from '../../../views/components/chips/CurrencyCodeChip'
import IconButton from '@mui/material/IconButton'
import DownloadForOfflineOutlinedIcon from '@mui/icons-material/DownloadForOfflineOutlined'
import { useHistory } from 'react-router-dom'
import {
  type CellOwnerAgreementDetails,
  type CellOwnerAgreementUrl
} from '../../../api-schema-types/asset-holdings/CellOwnerAgreementDomainDtos'
import { openInNewTab } from '../../../utility/dom/DomWindowHelpers'
import { setNotification } from '../../components/ui-widgets/uiWidgetsSlice'
import { RtkQueryHelper } from '../../../api-services/shared/RtkQueryHelper'
import { useAppDispatch } from '../../../app/hooks'
import Chip from '@mui/material/Chip'

const EditAssetHolderCoasView: React.FC<EditAssetHolderProps> = ({
  assetHolder
}: EditAssetHolderProps) => {
  const history = useHistory()
  const dispatch = useAppDispatch()

  const [signedUrl, setSignedUrl] = useState<string | null>(null)
  const [apiGetCoaPdfUrlTrigger] = useCoaGetPdfUrlLazyQuery()

  const goTo = (location: string): void => {
    history.push(location)
  }
  const handleSelectedCOA = (assetHolderId: number): void => {
    goTo(`/app/asset-holder/${assetHolderId}`)
  }

  const handleDownloadCoaPdf = async (coaDetails: CellOwnerAgreementDetails): Promise<void> => {
    apiGetCoaPdfUrlTrigger({
      cellOwnerAgreementId: coaDetails.id
    })
      .unwrap()
      .then((triggerResult) => {
        const url = (triggerResult as CellOwnerAgreementUrl).s3Url
        setSignedUrl(url)
      })
      .catch((err) => {
        dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
      })
  }

  useEffect(() => {
    if (signedUrl !== null) {
      openInNewTab(signedUrl)
    }
  }, [signedUrl])

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', flex: 15, editable: false },
    {
      field: 'projectStatus',
      headerName: 'Project Status',
      valueGetter: (params: any) => params.row.id,
      flex: 35,
      renderCell: (params) => {
        return (
          <ProjectStatusChip projectStatus={params.row.project.projectStatus}/>
        )
      }
    },
    {
      field: 'projectCurrency',
      headerName: 'Project Currency',
      valueGetter: (params: any) => params.row.id,
      flex: 30,
      align: 'center',
      renderCell: (params) => {
        return (
          <CurrencyCodeChip code={params.row.project.currencyCode} name={params.row.project.currencyCode}/>
        )
      }
    },
    {
      field: 'projectId',
      headerName: 'Project #',
      valueGetter: (params: any) => params.row.id,
      flex: 17,
      renderCell: (params) => {
        return (
            <div>{params.row.project.projectId}</div>
        )
      }
    },
    {
      field: 'projectName',
      headerName: 'Project',
      valueGetter: (params: any) => params.row.id,
      flex: 70,
      renderCell: (params) => {
        return (
          <Tooltip title={params.row.project.name} TransitionComponent={Zoom} arrow placement='bottom'>
            <div>{params.row.project.name}</div>
          </Tooltip>
        )
      }
    },
    { field: 'calculatedNumberOfCells', headerName: '# Cells', flex: 15, editable: false, align: 'right' },
    { field: 'calculatedPercentageOwnership', headerName: '% Share', flex: 40, editable: false, align: 'right' },
    { field: 'calculatedValueOfCellsFormatted', headerName: 'Value', flex: 25, editable: false, align: 'right' },
    { field: 'effectiveDate', headerName: 'Effective Date', flex: 30, editable: false },
    { field: 'generatedAt', headerName: 'Generated Date', flex: 30, editable: false },
    { field: 'expiredAt', headerName: 'Expired', flex: 30, editable: false },
    {
      field: 'cellOwnerAgreementTemplateVersion',
      headerName: 'Version',
      flex: 20,
      renderCell: (params) => {
        return (
            <Chip label={`${params.value.majorVersion}.${params.value.minorVersion}`} variant='outlined'/>
        )
      }
    },
    {
      field: 'downloadPdf',
      headerName: '',
      flex: 10,
      valueGetter: (params: any) => params.row.id,
      renderCell: (params) => (

        <Tooltip title={ params.row.expiredAt !== '' ? 'Expired COA' : 'Download pdf'}>
          <IconButton color={ params.row.expiredAt !== '' ? 'error' : 'primary'} disabled={params.row.pdfGenerated === false} onClick={async event => { await handleDownloadCoaPdf(params.row) }}>
            {/* { params.row.expiredAt !== '' ? <FileDownloadOffIcon /> : <DownloadForOfflineOutlinedIcon />} */}
            <DownloadForOfflineOutlinedIcon />
          </IconButton>
        </Tooltip>
      )
    },
    {
      field: 'viewCOADetails',
      headerName: '',
      flex: 30,
      valueGetter: (params: any) => params.row.id,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <div>
            <Tooltip title={params.row.sourceCellOwnerAgreementId !== '' ? `Supersedes agreement: ${params.row.sourceCellOwnerAgreementId}` : 'Original COA' }>
              <Button variant="outlined" onClick={() => { handleSelectedCOA(params.value) }} disabled={true}>
                View
              </Button>
            </Tooltip>
          </div>
        )
      }
    }
  ]

  return (
    <>
        <Box sx={{ pb: 4 }}>
          <h3>Cell Owner Agreements</h3>
          <Grid container spacing={4} >
            <Grid item md={12}>
              <DataGrid rows={assetHolder.cellOwnerAgreements} columns={columns} autoHeight={true} pageSize={20} rowsPerPageOptions={[20]} />
            </Grid>
          </Grid>
        </Box>

        {/* ==================================================================================================== */}

    </>
  )
}

export default EditAssetHolderCoasView
